import { ArrowExitIcon } from "@visma-netvisor/nv-react-icons";
import {
  Layout,
  Link,
  Typography,
} from "@visma-netvisor/react-component-library";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { sendTag } from "../../analytics";
import { NETVISOR_LOGIN_URL, COMPANY_CREATION_URL, strings } from "../../constants";
import { cleanBrowserCache, clearLeadIdentity } from "../../utils/cookieUtil";
import { redirectToNetvisor } from "../../utils/redirectToNetvisor";
import { logFrontendError } from "../LogFrontendError";
import { useNetvisorLogin, NetvisorLoginError } from "../hooks/useNetvisorLogin";
import AccountingServiceNeedForm from "./AccountingServiceNeedForm";
import ActionButton from "./ActionButton";
import { useServiceNeedContext, QuestionSteps } from "../../context/ServiceNeedContext";

type ServiceNeedsResponse = {
  updated?: boolean;
  backendStatus?: number;
  error?: string;
};

const SERVICE_NEEDS_TIMEOUT_MS = 20000;

type CompanyCreationSuccessProps = {
  orderId: string;
};

const CompanyCreationSuccess = ({ orderId }: CompanyCreationSuccessProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const netvisorLoginMutation = useNetvisorLogin();

  const {
    setQuestionStep,
    accountingServiceNeed,
    holviServiceNeed,
    hasInsurance,
    insuranceServiceNeed,
  } = useServiceNeedContext();

  sendTag({ status: "companyCreationSuccess" });

  // Awaited on purpose - the redirect in login() would cancel an unawaited fetch. Failures are logged, never fatal.
  const updateAccountingServiceNeed = async (
    accountingServiceNeed: boolean,
    holviServiceNeed: boolean,
    hasInsurance: boolean,
    insuranceServiceNeed: boolean
  ) => {
    try {
      const response = await fetch(`${COMPANY_CREATION_URL}/v1/orders/${orderId}/service-needs`, {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          accountingServiceNeed,
          holviServiceNeed,
          hasInsurance,
          insuranceServiceNeed,
        }),
        // Stays above the 8-13s Salesforce write latency so slow successes are not aborted, but well under the server's 100s HttpClient default, which would otherwise park the user on the spinner before login.
        signal: AbortSignal.timeout(SERVICE_NEEDS_TIMEOUT_MS),
      });

      // The endpoint answers 200 { updated: false } when the backend forward fails, so response.ok alone is not success.
      const body = response.ok
        ? ((await response.json().catch(() => null)) as ServiceNeedsResponse | null)
        : null;

      if (body?.updated !== true) {
        await logFrontendError({
          info:
            body?.error === "BackendNotUpdated"
              ? "Service-needs not recorded by backend - lead likely not yet converted (non-blocking)"
              : "Updating service-needs failed (non-blocking)",
          level: "warning",
          details: {
            accountingServiceNeed,
            status: response.status,
            backendStatus: body?.backendStatus,
            backendError: body?.error,
          },
        });
      }
    } catch (error) {
      await logFrontendError({
        info: "Updating service-needs failed (non-blocking)",
        level: "warning",
        error,
        details: { accountingServiceNeed },
      });
    }
  };

  const login = async () => {
    sendTag({ status: "redictedToNetvisor" });

    try {
      const token = await netvisorLoginMutation.mutateAsync();

      if (token) {
        sessionStorage.removeItem(strings.ORDER_ID_COOKIE_NAME);
        sessionStorage.removeItem(strings.ORDER_DATA_COOKIE_NAME);
        clearLeadIdentity();
        redirectToNetvisor(NETVISOR_LOGIN_URL, token);
      }
    } catch (error) {
      const isSessionExpired =
        error instanceof NetvisorLoginError && error.status === 401;
      if (!isSessionExpired) {
        await logFrontendError({
          info: "Netvisor redirect failed",
          error,
        });
      }
      setLoginError(true);
    }
  };

  useEffect(() => {
    if (loginError) {
      cleanBrowserCache();
    }
  }, [loginError]);

  const goToInitialQuestionStep = () => {
    setQuestionStep(QuestionSteps.INITIAL);
  };

  const goToFinalQuestionStep = () => {
    setQuestionStep(QuestionSteps.FINAL);
  }

  const handleUpdateAndLogin = async () => {
    setIsLoading(true);

    await updateAccountingServiceNeed(
      accountingServiceNeed ?? false,
      holviServiceNeed ?? false,
      hasInsurance ?? false,
      insuranceServiceNeed ?? false
    );

    await login();
  };

  return (
    <>
      <Layout.Flex alignItems="center" flexDirection="column">
        <StyledTypography
          as="h1"
          color="secondary"
          variant="headingH1Semibold"
        >
          {t("OnlineStatus.CompanySuccessfullyCreated")}
        </StyledTypography>
        <AccountingServiceNeedForm />
        {loginError && (
          <StyledTypography
            as="h4"
            color="error"
            variant="contentHeadingH4Semibold"
            styleOverrides={{ paddingBottom: 130 }}
          >
            {t("OnlineStatus.ErrorTransitioningBankAccountSettings")}{" "}
            <Link href="https://login.netvisor.fi">
              <ArrowExitIcon />
            </Link>
          </StyledTypography>
        )}
        <ActionButton
          isLoading={isLoading}
          goToInitialQuestionStep={goToInitialQuestionStep}
          goToFinalQuestionStep={goToFinalQuestionStep}
          handleUpdateAndLogin={handleUpdateAndLogin}
        />
      </Layout.Flex>
    </>
  );
};

export default CompanyCreationSuccess;

const StyledTypography = styled(Typography)`
  padding-top: 60px;
  @media (max-width: 768px) {
    font-size: 20px;
    padding-bottom: 16px;
  }
  @media (max-height: 480px) {
    padding-top: 16px;
  }
`;
